import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Globe, ExternalLink, Facebook, Instagram, Download } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { mockContent } from '../mock';
import PWAInstallPrompt from './PWAInstallPrompt';

const Layout = ({ children }) => {
  const { language, toggleLanguage } = useLanguage();
  const content = mockContent[language];
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      setCanInstall(false);
      setDeferredPrompt(null);
    };

    // Check if already installed
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches ||
                       window.navigator.standalone ||
                       document.referrer.includes('android-app://');
    
    if (!isInstalled) {
      setCanInstall(true); // Show install button even without prompt for mobile
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallApp = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('Install prompt outcome:', outcome);
      setDeferredPrompt(null);
      setCanInstall(false);
    } else {
      // Fallback for browsers that don't support install prompt
      const message = language === 'en' 
        ? 'To install: Tap menu (⋮) → "Add to Home Screen" or "Install App"'
        : 'Para instalar: Toca menú (⋮) → "Añadir a pantalla de inicio" o "Instalar app"';
      alert(message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-green-50">
      <nav className="bg-white/80 backdrop-blur-md border-b border-orange-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_b7f462f8-48db-40de-8f5a-801465c68406/artifacts/moybkxk2_LOGO%20REDWOOD.png" 
                alt="Redwood Conecta Logo"
                className="w-10 h-10 object-contain"
              />
              <h1 className="text-xl font-bold text-gray-800">Redwood Conecta</h1>
            </div>
            
            <div className="flex items-center space-x-2">
              {canInstall && (
                <Button
                  onClick={handleInstallApp}
                  size="sm"
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white flex items-center space-x-1"
                >
                  <Download className="w-4 h-4" />
                  <span>{language === 'en' ? 'Install' : 'Instalar'}</span>
                </Button>
              )}
              
              <Button
                onClick={toggleLanguage}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2 border-orange-200 hover:bg-orange-50 transition-all duration-200"
              >
                <Globe className="w-4 h-4" />
                <span className="font-medium">{language === 'en' ? 'ES' : 'EN'}</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>
      
      <main className="flex-1">
        {children}
      </main>
      
      <footer className="bg-white/60 backdrop-blur-sm border-t border-orange-100 mt-16">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center mb-6">
            <p className="text-gray-600 text-sm mb-4">
              {language === 'en' 
                ? 'Evidence-based resources for autism education and family support'
                : 'Recursos basados en evidencia para educación sobre autismo y apoyo familiar'
              }
            </p>
            
            {/* Social Media Links - Single Responsive Layout */}
            <div className="mb-4">
              {/* Website Link */}
              <div className="flex justify-center mb-3">
                <a
                  href="https://redwoodconecta.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-gray-600 hover:text-orange-600 transition-colors duration-200"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span className="text-sm font-medium">redwoodconecta.com</span>
                </a>
              </div>
              
              {/* Social Media Icons */}
              <div className="flex justify-center items-center space-x-6">
                {/* Facebook */}
                <a
                  href="https://www.facebook.com/RedwoodConecta"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors duration-200"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                  <span className="text-sm font-medium hidden sm:inline">Facebook</span>
                </a>
                
                {/* Instagram */}
                <a
                  href="https://www.instagram.com/redwoodconecta/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-gray-600 hover:text-pink-600 transition-colors duration-200"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5" />
                  <span className="text-sm font-medium hidden sm:inline">Instagram</span>
                </a>
              </div>
            </div>
            
            {/* Copyright */}
            <div className="pt-4 border-t border-orange-100 text-xs text-gray-500">
              © 2025 Redwood Conecta. {language === 'en' ? 'All rights reserved.' : 'Todos los derechos reservados.'}
            </div>
          </div>
        </div>
      </footer>
      
      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
    </div>
  );
};

export default Layout;